import { BitReader } from '../aac/bitReader';
import type { Fraction } from '../util/fractionType';
import { frac } from '../util/integerMath';
// From Table 7-1 – NAL unit type codes, syntax element categories, and NAL unit type classes
// in h264 specs
export const slice_layer_without_partitioning_rbsp_non_idr = 1;
export const slice_data_partition_a_layer_rbsp = 2;
export const slice_data_partition_b_layer_rbsp = 3;
export const slice_data_partition_c_layer_rbsp = 4;
export const slice_layer_without_partitioning_rbsp_idr = 5;
export const sei_rbsp = 6;
export const seq_parameter_set_rbsp = 7;
export const pic_parameter_set_rbsp = 8;
export const access_unit_delimiter_rbsp = 9;
export const end_of_seq_rbsp = 10;
export const end_of_stream_rbsp = 11;
export const filler_data_rbsp = 12;
export const seq_parameter_set_extension_rbsp = 13;
export const prefix_nal_unit_rbsp = 14;
export const subset_seq_parameter_set_rbsp = 15;
export const slice_layer_without_partitioning_rbsp_auxiliary = 19;
export const slice_layer_extension_rbsp = 20;

export interface H264FrameInfo {
  profile: number;
  level: number;
  profileCompatibility: number;
  /**
   * true if the frames are progressive.
   */
  frame_mbs_only_flag: boolean;
  /**
   * false if progressive. For interlaced, true
   * means that each frame is encoded with 2 fields that may
   * or may not be encoded separately. In such a case, we need
   * to inspect the slice headers to figure out whether we have
   * fields or frames.
   */
  mb_adaptive_frame_field_flag: boolean;
  width: number;
  height: number;
  sampleAspectRatio: Fraction;
  separate_colour_plane_flag: boolean;
  log2_max_frame_num_minus4: number;
}

/**
 * The h264 NAL units are escaped to prevent the appearance of the following
 * byte sequences:
 *
 *   0x000000
 *   0x000001
 *   0x000002
 *   0x000003
 *
 * These sequences are respectively encoded with:
 *
 *   0x00000300
 *   0x00000301
 *   0x00000302
 *   0x00000303
 *
 * This function returns an array containing the unescaped data.
 */
export function decodeRBSP(escaped: Uint8Array): Uint8Array {
  const rbsp = new Uint8Array(escaped.byteLength);

  const len = escaped.byteLength;
  let src = 0;
  let dst = 0;
  for (let i = 0; i < len; i++) {
    if (escaped[src] === 0 && escaped[src + 1] === 0 && escaped[src + 2] === 3) {
      rbsp[dst++] = 0;
      rbsp[dst++] = 0;
      src += 3;
      i += 2;
    } else {
      rbsp[dst++] = escaped[src++]!;
    }
  }

  return rbsp.subarray(0, dst);
}


function isProfileWithOptionalSpsData(profileIdc: number) {
  switch (profileIdc) {
    case 100:
    case 110:
    case 122:
    case 244:
    case 44:
    case 83:
    case 86:
    case 118:
    case 128:
    case 138:
    case 139:
    case 134: return true;
    default: return false;
  }
}


/**
 * Skips a scaling list.
 */
function skipScalingList(count: number, bitReader: BitReader) {
  let lastScale = 8;
  let nextScale = 8;

  for (let j = 0; j < count; j++) {
    if (nextScale !== 0) {
      const deltaScale = bitReader.readSignedExpGolomb();
      nextScale = (lastScale + deltaScale + 256) % 256;
    }

    lastScale = (nextScale === 0) ? lastScale : nextScale;
  }
}


/**
 * A sequence parameter set NAL containing information about the video
 * format (profile, dimensions, ...) encoded with exponential Golomb codes.
 * Since we only need the width and height, we parse what we need to obtain
 * this information.
 */
export function parseSequenceparameterSet(rbsp: Uint8Array): H264FrameInfo {
  const bitReader = new BitReader(rbsp);

  let separate_colour_plane_flag = false;
  const profileIdc = bitReader.readBits(8);
  const profileCompatibility = bitReader.readBits(8);
  const levelIdc = bitReader.readBits(8);
  bitReader.readUnsignedExpGolomb(); // seq_parameter_set_id

  if (isProfileWithOptionalSpsData(profileIdc)) {
    const chromaFormatIdc = bitReader.readUnsignedExpGolomb();
    if (chromaFormatIdc === 3) {
      separate_colour_plane_flag = bitReader.readBits(1) === 1; // separate_colour_plane_flag
    }
    bitReader.readUnsignedExpGolomb(); // bit_depth_luma_minus8
    bitReader.readUnsignedExpGolomb(); // bit_depth_chroma_minus8
    bitReader.readBits(1); // qpprime_y_zero_transform_bypass_flag

    if (bitReader.readBits(1) === 1) { // seq_scaling_matrix_present_flag
      const scalingListCount = (chromaFormatIdc !== 3) ? 8 : 12;
      for (let i = 0; i < scalingListCount; i++) {
        if (bitReader.readBits(1) === 1) { // seq_scaling_list_present_flag[ i ]
          if (i < 6) {
            skipScalingList(16, bitReader);
          } else {
            skipScalingList(64, bitReader);
          }
        }
      }
    }
  }

  const log2_max_frame_num_minus4 = bitReader.readUnsignedExpGolomb();
  const picOrderCntType = bitReader.readUnsignedExpGolomb();

  if (picOrderCntType === 0) {
    bitReader.readUnsignedExpGolomb(); // log2_max_pic_order_cnt_lsb_minus4
  } else if (picOrderCntType === 1) {
    bitReader.readBits(1); // delta_pic_order_always_zero_flag
    bitReader.readSignedExpGolomb(); // offset_for_non_ref_pic
    bitReader.readSignedExpGolomb(); // offset_for_top_to_bottom_field
    const numRefFramesInPicOrderCntCycle = bitReader.readUnsignedExpGolomb();
    for (let i = 0; i < numRefFramesInPicOrderCntCycle; i++) {
      bitReader.readSignedExpGolomb(); // offset_for_ref_frame[ i ]
    }
  }

  bitReader.readUnsignedExpGolomb(); // max_num_ref_frames
  bitReader.readBits(1); // gaps_in_frame_num_value_allowed_flag

  const picWidthInMbsMinus1 = bitReader.readUnsignedExpGolomb();
  const picHeightInMapUnitsMinus1 = bitReader.readUnsignedExpGolomb();

  const frame_mbs_only_flag = bitReader.readBits(1) === 1;
  // When not present, mb_adaptive_frame_field_flag must be treated as 0
  let mb_adaptive_frame_field_flag = false;
  if (frame_mbs_only_flag === false) {
    mb_adaptive_frame_field_flag = bitReader.readBits(1) === 1;
  }

  bitReader.readBits(1); // direct_8x8_inference_flag
  let frameCropLeftOffset = 0;
  let frameCropRightOffset = 0;
  let frameCropTopOffset = 0;
  let frameCropBottomOffset = 0;
  if (bitReader.readBits(1) === 1) { // frame_cropping_flag
    frameCropLeftOffset = bitReader.readUnsignedExpGolomb();
    frameCropRightOffset = bitReader.readUnsignedExpGolomb();
    frameCropTopOffset = bitReader.readUnsignedExpGolomb();
    frameCropBottomOffset = bitReader.readUnsignedExpGolomb();
  }

  let sarRatio = [1, 1];
  if (bitReader.readBits(1) === 1) {
    // vui_parameters_present_flag
    if (bitReader.readBits(1) === 1) {
      // aspect_ratio_info_present_flag
      const aspectRatioIdc = bitReader.readBits(8);
      switch (aspectRatioIdc) {
        case 1: sarRatio = [1, 1]; break;
        case 2: sarRatio = [12, 11]; break;
        case 3: sarRatio = [10, 11]; break;
        case 4: sarRatio = [16, 11]; break;
        case 5: sarRatio = [40, 33]; break;
        case 6: sarRatio = [24, 11]; break;
        case 7: sarRatio = [20, 11]; break;
        case 8: sarRatio = [32, 11]; break;
        case 9: sarRatio = [80, 33]; break;
        case 10: sarRatio = [18, 11]; break;
        case 11: sarRatio = [15, 11]; break;
        case 12: sarRatio = [64, 33]; break;
        case 13: sarRatio = [160, 99]; break;
        case 14: sarRatio = [4, 3]; break;
        case 15: sarRatio = [3, 2]; break;
        case 16: sarRatio = [2, 1]; break;
        case 255: {
          sarRatio = [bitReader.readBits(16), bitReader.readBits(16)];
          break;
        }
      }
    }
  }

  return {
    profile: profileIdc,
    level: levelIdc,
    profileCompatibility,
    frame_mbs_only_flag,
    mb_adaptive_frame_field_flag,
    separate_colour_plane_flag,
    log2_max_frame_num_minus4,
    width: (((picWidthInMbsMinus1 + 1) * 16) - frameCropLeftOffset * 2 - frameCropRightOffset * 2),
    height: (2 - (frame_mbs_only_flag ? 1 : 0)) * (((picHeightInMapUnitsMinus1 + 1) * 16) - (frameCropTopOffset * 2) - (frameCropBottomOffset * 2)),
    sampleAspectRatio: frac(sarRatio[0]!, sarRatio[1]!),
  };
}


export function getCodecString(info: H264FrameInfo): string {
  const profile = info.profile.toString(16).padStart(2, '0');
  const level = info.level.toString(16).padStart(2, '0');
  const compat = info.profileCompatibility.toString(16).padStart(2, '0');
  return `avc1.${profile}${compat}${level}`;

}

export interface SliceHeaderInfo {
  first_mb_in_slice: number;
  slice_type: number;
  pic_parameter_set_id: number;
  colour_plane_id: number | undefined;
  frame_num: number;
  field_pic_flag: boolean | undefined;
  bottom_field_flag: boolean | undefined;
}


export function inspectSliceHeader(
  rbsp: Uint8Array,
  frameInfo: H264FrameInfo,
): SliceHeaderInfo {
  const bitReader = new BitReader(rbsp);
  const first_mb_in_slice = bitReader.readUnsignedExpGolomb();
  const slice_type  = bitReader.readUnsignedExpGolomb();
  const pic_parameter_set_id  = bitReader.readUnsignedExpGolomb();
  let colour_plane_id: number | undefined = undefined;
  if (frameInfo.separate_colour_plane_flag) {
    colour_plane_id = bitReader.readBits(2);
  }

  const frame_num = bitReader.readBits(frameInfo.log2_max_frame_num_minus4 + 4);

  let field_pic_flag: boolean | undefined;
  let bottom_field_flag: boolean | undefined;
  if (!frameInfo.frame_mbs_only_flag) {
    field_pic_flag = bitReader.readBits(1) === 1;

    if (field_pic_flag) {
      bottom_field_flag = bitReader.readBits(1) === 1;
    }
  }

  return {
    first_mb_in_slice,
    slice_type,
    pic_parameter_set_id,
    colour_plane_id,
    frame_num,
    field_pic_flag,
    bottom_field_flag,
  };
}

